// components/ui/SafeImage.js - Simplified image component for static export
import { useState, useCallback } from 'react';
import Image from 'next/image';

const PLACEHOLDER_SVG = (width = 300, height = 200, text = '') => {
  const svg = `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="${width}" height="${height}" fill="#F3F4F6"/>
    <rect x="${width * 0.2}" y="${height * 0.2}" width="${width * 0.6}" height="${height * 0.5}" fill="#E5E7EB" rx="8"/>
    <circle cx="${width * 0.35}" cy="${height * 0.35}" r="${Math.min(width, height) * 0.05}" fill="#F3F4F6"/>
    <path d="M${width * 0.2} ${height * 0.6}L${width * 0.4} ${height * 0.45}L${width * 0.6} ${height * 0.55}L${width * 0.8} ${height * 0.6}V${height * 0.7}H${width * 0.2}V${height * 0.6}Z" fill="#D1D5DB"/>
    ${text ? `<text x="${width / 2}" y="${height * 0.85}" font-family="system-ui,sans-serif" font-size="${Math.max(12, Math.min(width, height) * 0.06)}" fill="#9CA3AF" text-anchor="middle">${text}</text>` : ''}
  </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

const SafeImage = ({
  src,
  alt,
  fill,
  width = 300,
  height = 200,
  className = '',
  ...props
}) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Determine the image source
  const getImageSrc = useCallback(() => {
    if (!src || src === 'undefined' || src === 'null' || hasError) {
      return PLACEHOLDER_SVG(width, height, 'Image non disponible');
    }
    return src;
  }, [src, hasError, width, height]);

  const imgSrc = getImageSrc();
  const isPlaceholder = imgSrc.startsWith('data:');

  const handleError = useCallback(() => {
    setHasError(true);
    setIsLoading(false);
  }, []);

  const handleLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  // Build image props
  const imageProps = {
    src: imgSrc,
    alt: alt || 'Image',
    onError: handleError,
    onLoad: handleLoad,
    className: className,
    unoptimized: isPlaceholder,
    ...props
  };

  // Handle fill vs fixed dimensions
  if (fill) {
    imageProps.fill = true;
    imageProps.sizes = props.sizes || "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw";
    imageProps.style = { objectFit: 'cover', ...props.style };
  } else {
    imageProps.width = width;
    imageProps.height = height;
  }

  return (
    <div className={fill ? "relative w-full h-full" : "relative"} style={fill ? undefined : { width, height }}>
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <Image {...imageProps} />
      {isLoading && !isPlaceholder && (
        <div className="absolute inset-0 bg-gray-100 dark:bg-gray-800 animate-pulse rounded" />
      )}
    </div>
  );
};

export default SafeImage;
